import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import {
  Children,
  type ForwardedRef,
  forwardRef,
  type HTMLAttributes,
  isValidElement,
  type ReactNode,
} from "react";

const tagVariants = cva(
  "inline-flex items-center justify-center text-nowrap [&_svg]:size-4",
  {
    variants: {
      variant: {
        primary: "bg-purple-500 text-white",
        "primary-black": "bg-black-950 text-white",

        secondary: "border border-purple-200 bg-purple-50 text-purple-700",
        tertiary: "border border-black-200 bg-white text-black-950",
        quaternary: "bg-black-100 text-black-700",

        success: "border border-green-200 bg-green-50 text-green-700",
        danger: "border border-red-200 bg-red-50 text-red-600",
        info: "border border-blue-200 bg-blue-50 text-blue-700",
        magenta: "border border-magenta-200 bg-magenta-50 text-magenta-700",
        orange: "border border-orange-200 bg-orange-50 text-orange-700",
        yellow: "border border-yellow-200 bg-yellow-50 text-yellow-700",
        teal: "border border-teal-200 bg-teal-50 text-teal-700",
        lime: "border border-lime-200 bg-lime-50 text-lime-700",

        disabled: "bg-black-200 text-black-400",
        gradient: "bg-blackPurple text-white",
      },
      size: {
        sm: "h-5 min-w-5 gap-0.5 rounded-md py-0.5 text-button-xxs",
        md: "h-6 min-w-6 gap-1 rounded-lg py-1 text-button-xs",
      },
      hasLeftIcon: {
        true: null,
        false: null,
      },
      hasRightIcon: {
        true: null,
        false: null,
      },
      isIconOnly: {
        true: null,
        false: null,
      },
    },
    compoundVariants: [
      //sm variants
      {
        size: "sm",
        hasLeftIcon: true,
        class: "pl-0.5",
      },
      {
        size: "sm",
        hasRightIcon: true,
        class: "pr-0.5",
      },
      //icon-text content paddings
      { size: "sm", isIconOnly: true, class: "px-0.25" },
      { size: "sm", isIconOnly: false, class: "px-2" },
      //md variants
      {
        size: "md",
        hasLeftIcon: true,
        class: "pl-1",
      },
      {
        size: "md",
        hasRightIcon: true,
        class: "pr-1",
      },
      //icon-text content paddings
      { size: "md", isIconOnly: true, class: "px-0.75" },
      { size: "md", isIconOnly: false, class: "px-2.5" },
    ],
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

type TagVariantType = VariantProps<typeof tagVariants>["variant"];

type TagProps = HTMLAttributes<HTMLSpanElement> &
  Omit<VariantProps<typeof tagVariants>, "icon"> & {
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
    asChild?: boolean;
  };

//returns the deepest child in a nested component tree, does not support multiple nested components
function getDeepestChildren(children: ReactNode) {
  if (
    Children.toArray(children).length === 1 &&
    isValidElement(children) &&
    children.props.children
  ) {
    return getDeepestChildren(children.props.children);
  }
  return children;
}

function Tag(
  {
    className,
    variant,
    size,
    children,
    leftIcon = null,
    rightIcon = null,
    asChild = false,
    ...props
  }: TagProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const Comp = asChild ? Slot : "span";

  //returns the first children that are not a singular component
  const childrenArray = Children.toArray(getDeepestChildren(children));

  const isIconOnly =
    childrenArray.length === 1 &&
    typeof childrenArray[0] !== "string" &&
    typeof childrenArray[0] !== "number";

  return (
    <Comp
      className={tagVariants({
        variant,
        size,
        hasLeftIcon: !!leftIcon,
        hasRightIcon: !!rightIcon,
        isIconOnly,
        className,
      })}
      ref={ref}
      {...props}
    >
      {leftIcon}
      <Slottable>
        <span className="truncate">{children}</span>
      </Slottable>
      {rightIcon}
    </Comp>
  );
}
Tag.displayName = "Tag";

const TagWithRef = forwardRef(Tag);

export { TagWithRef as Tag, type TagVariantType };
